import React, { useState } from "react";
import { Box, Flex, Text, Icon, Divider, Stack, Select, Button } from "@chakra-ui/react";
import { BadgeCheck, BadgeInfo, BadgeX, Calendar, Clock, Mailbox, User, Edit, PhoneCall } from "lucide-react";
import ReusableAvatar from "components/Avatar/ReusableAvatar";
import { formatDate } from "utils/dateFormatter";
import { useDispatch } from "react-redux";
import { updateContactSupportMessageAction, fetchContactSupportMessagesAction } from "state/actions/contactSupportActions";
import { useCustomNotification } from "components/Toasts/OpenNotification";
import GradientButton from "components/Buttons/GradientButton";

const ViewMessage = ({ message }) => {
  if (!message) return null;

  // Destructure message and user info
  const { message: messageDetails, user } = message;
  const { first_name, last_name, username } = user;

  const dispatch = useDispatch();
  const showNotification = useCustomNotification();


  // Local state for selecting a new status
  const [selectedStatus, setSelectedStatus] = useState(messageDetails.status);
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const userName = first_name && last_name ? `${first_name} ${last_name}` : username;

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);

    try {
      const response = await dispatch(updateContactSupportMessageAction(messageDetails.message_id, { status: newStatus }));

      if (response.success) {
        showNotification({
          message: "Status Updated Successfully",
          description: `The status has been updated to ${newStatus}.`,
          type: "success",
          duration: 3,
        });


        dispatch(fetchContactSupportMessagesAction());

      } else {
        showNotification({
          message: "Error Updating Status",
          description: response.error || "There was an error updating the status.",
          type: "error",
          duration: 5,
        });
      }
    } catch (error) {
      showNotification({
        message: "Error Updating Status",
        description: error.message || "There was an error updating the status.",
        type: "error",
        duration: 5,
      });
    }

    setIsEditingStatus(false);
  };




  return (
    <Flex direction="column" p={8} width="100%">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex alignItems="center">
          <ReusableAvatar name={userName} />
          <Box >
            <Text fontSize="2xl" fontWeight="bold" display="flex" alignItems="center">
              {userName}
              {selectedStatus === "Resolved" && (
                <>
                  <Icon as={BadgeCheck} size={16} color="green.500" stroke="white" ml={2} fill="green.500" />
                  <Text color="green.500" fontWeight="xs" fontSize="13px" ml={1}>
                    Resolved
                  </Text>
                </>
              )}
              {selectedStatus === "Pending" && (
                <>
                  <Icon as={BadgeInfo} size={16} color="yellow.500" stroke="white" ml={2} fill="yellow.500" />
                  <Text color="yellow.500" fontWeight="xs" fontSize="13px" ml={1}>
                    Pending
                  </Text>
                </>
              )}
              {selectedStatus === "Unread" && (
                <>
                  <Icon as={BadgeInfo} size={16} color="purple.500" stroke="white" ml={2} fill="purple.500" />
                  <Text color="purple.500" fontWeight="xs" fontSize="13px" ml={1}>
                    Unread
                  </Text>
                </>
              )}
              {selectedStatus === "Archived" && (
                <>
                  <Icon as={BadgeX} size={16} color="red.500" stroke="white" ml={2} fill="red.500" />
                  <Text color="red.500" fontWeight="xs" fontSize="13px" ml={1}>
                    Archived
                  </Text>
                </>
              )}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Support Type: {messageDetails.support_type}
            </Text>
          </Box>
        </Flex>

      </Flex>

      <Flex mt={4} alignItems="center">
        <Icon as={Calendar} size={16} color="gray.500" mr={1} />
        <Text fontSize="sm" color="gray.500" mr={4}>
          Date Sent: {formatDate(messageDetails.created_at)}
        </Text>
      </Flex>

      <Divider my={6} />

      <Flex direction="column" p={4}>
        {/* Message Title */}
        <Text fontSize="lg" fontWeight="bold" mb={2}>
          {messageDetails.support_type}
        </Text>

        {/* Message Details */}
        <Box mb={6}>
          <Stack spacing={4}>
            <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
              <Icon as={PhoneCall} mr={2} color="gray.400" />
              <Text fontWeight="500" color="gray.500" fontSize="sm">
                Phone Number: {user.phone_number}
              </Text>
            </Flex>
            <Flex alignItems="center" border="1px" borderColor="gray.300" borderRadius="md" p={2}>
              <Icon as={Clock} mr={2} color="gray.400" />
              <Text fontWeight="500" color="gray.500" fontSize="sm">
                Package Number: {messageDetails.package_number}
              </Text>
            </Flex>
            <Box border="1px" borderColor="gray.300" borderRadius="md" p={6} display="flex" alignItems="center">
              <Icon as={Mailbox} mr={3} color="gray.400" fontSize="lg" />
              <Text fontWeight="500" color="gray.500" fontSize="md">
                Message: {messageDetails.message}
              </Text>
            </Box>
          </Stack>
        </Box>
      </Flex>
      {/* Status Update Section (Button + Dropdown aligned horizontally) */}
      <Flex alignItems="center" mt={4} ml={"5"}>
        <GradientButton
          leftIcon={<Edit size={16} />}
          height="36px"
          fontSize="sm"
          onClick={() => setIsEditingStatus(!isEditingStatus)}
          mr={3}
        >
          Update Status
        </GradientButton>

        {isEditingStatus && (
          <Box width="200px">
            <Select
              value={selectedStatus}
              onChange={handleStatusChange}
              size="sm"
              borderRadius="8px"
            >
              <option value="Pending">Pending</option>
              <option value="Resolved">Resolved</option>
              <option value="Unread">Unread</option>
            </Select>
          </Box>

        )}
      </Flex>

    </Flex>
  );
};

export default ViewMessage;
